import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import catalog from "../../../../services/catalogService";
import useVendorAccessGate from "../../../../hooks/useVendorAccessGate";
import { useAuth } from "../../../../services/providers/authProvider";
import PageListEmptyState from "../../../Shared/pageListEmptyState";
import CardCaricamento from "./cardCaricamento";
import DialogDettaglioImportazione from "./dialogDettaglioImportazione";
import { normalizeImportStatistics, normalizeImportIssues } from "./importStatisticsLabels";

const BLOCK_SIZE = 10;

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeImportJob(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    jobId: raw.jobId ?? raw.job_id,
    startTime: raw.startTime ?? raw.start_time,
    endTime: raw.endTime ?? raw.end_time,
    vendorId: raw.vendorId ?? raw.vendor_id,
    totaleRecord: toNum(raw.totaleRecord ?? raw.totale_record, 0),
    totaleErrori: toNum(raw.totaleErrori ?? raw.totale_errori, 0),
    previousTotaleRecord: toNum(raw.previousTotaleRecord ?? raw.previous_totale_record, 0),
    previousTotaleErrori: toNum(raw.previousTotaleErrori ?? raw.previous_totale_errori, 0),
  };
}

function mergeImportsByJobId(existing, batch) {
  const seen = new Set(existing.map((j) => j.jobId).filter(Boolean));
  const out = [...existing];
  for (const job of batch) {
    if (!job) continue;
    const id = job.jobId;
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(job);
  }
  return out;
}

const Caricamenti = () => {
  useVendorAccessGate();
  const { token, vendorId } = useAuth() || {};

  const [imports, setImports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextOffset, setNextOffset] = useState(BLOCK_SIZE);

  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorState, setErrorState] = useState("");

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [importStatistics, setImportStatistics] = useState(null);
  const [issues, setIssues] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const noMoreResults =
    totalCount > 0 ? imports.length >= totalCount : imports.length === 0 && !isInitialLoading;

  const loadInitial = useCallback(async () => {
    if (!token) return;
    setIsInitialLoading(true);
    setErrorState("");
    try {
      const response = await catalog.getCatalogs({
        ...(vendorId != null ? { vendorId } : {}),
        limit: BLOCK_SIZE,
        offset: 0,
      });
      const body = response?.data ?? {};
      const importsRaw = Array.isArray(body.imports) ? body.imports : [];
      const total = toNum(body.totalCount ?? body.total_count, importsRaw.length);
      const mapped = importsRaw.map(normalizeImportJob).filter(Boolean);
      setImports(mapped);
      setTotalCount(total);
      setNextOffset(BLOCK_SIZE);
    } catch (e) {
      console.error("Errore nel caricamento caricamenti:", e);
      setErrorState("Impossibile caricare i caricamenti.");
      setImports([]);
      setTotalCount(0);
    } finally {
      setIsInitialLoading(false);
    }
  }, [token, vendorId]);

  const loadMore = useCallback(async () => {
    if (!token || isLoadingMore) return;
    if (imports.length >= totalCount && totalCount > 0) return;

    setIsLoadingMore(true);
    setErrorState("");
    try {
      const response = await catalog.getCatalogs({
        ...(vendorId != null ? { vendorId } : {}),
        limit: BLOCK_SIZE,
        offset: nextOffset,
      });
      const body = response?.data ?? {};
      const importsRaw = Array.isArray(body.imports) ? body.imports : [];
      const pageTotal = toNum(body.totalCount ?? body.total_count, totalCount);
      if (pageTotal > 0) {
        setTotalCount(pageTotal);
      }
      const mapped = importsRaw.map(normalizeImportJob).filter(Boolean);
      if (mapped.length === 0) {
        setTotalCount(imports.length);
        return;
      }
      setImports((prev) => mergeImportsByJobId(prev, mapped));
      setNextOffset((o) => o + BLOCK_SIZE);
    } catch (e) {
      console.error("Errore nel caricamento ulteriori caricamenti:", e);
      setErrorState("Impossibile caricare altri risultati.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [token, vendorId, isLoadingMore, nextOffset, imports.length, totalCount]);

  const handleOpenDetails = useCallback(
    async (jobId) => {
      if (!token || !jobId) return;
      setSelectedJobId(jobId);
      setIsDetailsLoading(true);
      setDetailsError("");
      setImportStatistics(null);
      setIssues([]);
      try {
        const data = {
          uuidJob: jobId,
          limit: 50,
          page: 0,
        };
        const response = await catalog.getCatalogImportDetails(data);
        if (response?.status === 202 || response?.data?.statusCode === 202) {
          setDetailsError("I dettagli per questo caricamento non sono più disponibili.");
          setImportStatistics(null);
          setIssues([]);
          return;
        }
        const body = response?.data ?? {};
        setImportStatistics(normalizeImportStatistics(body.statistics));
        setIssues(normalizeImportIssues(body.errors));
      } catch (e) {
        console.error("Errore nel caricamento dettagli importazione:", e);
        const status = e?.response?.status;
        if (status === 202) {
          setDetailsError("I dettagli per questo caricamento non sono più disponibili.");
        } else {
          setDetailsError("Impossibile caricare i dettagli importazione.");
        }
        setImportStatistics(null);
        setIssues([]);
      } finally {
        setIsDetailsLoading(false);
      }
    },
    [token]
  );

  const handleCloseDialog = () => {
    setSelectedJobId(null);
    setImportStatistics(null);
    setIssues([]);
    setDetailsError("");
  };

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const showEmptyMessage = !isInitialLoading && !errorState && imports.length === 0;
  const loadMoreDisabled =
    isInitialLoading || isLoadingMore || noMoreResults || (totalCount > 0 && imports.length >= totalCount);

  return (
    <Box display="flex" flexDirection="column" sx={{ width: "100%", position: "relative" }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: "Rubik",
            fontWeight: "bold",
            color: "#0b3d87",
          }}
        >
          Catalogo / Caricamenti
        </Typography>

        <Typography
          sx={{
            mt: 1,
            fontFamily: "Rubik",
            color: "#808080",
          }}
        >
          Visualizza lo storico dei caricamenti automatici del tuo catalogo.
        </Typography>

        <Box mt={3}>
          {isInitialLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : errorState ? (
            <Typography
              sx={{
                fontFamily: "Rubik",
                color: "#e00036",
                fontWeight: "bold",
              }}
            >
              {errorState}
            </Typography>
          ) : showEmptyMessage ? (
            <PageListEmptyState message="Nessun caricamento disponibile 🫤" />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {imports.map((job, index) => (
                <CardCaricamento
                  key={job.jobId ?? `${job.startTime}-${job.vendorId}`}
                  job={job}
                  previousJob={imports[index + 1] ?? null}
                  onOpenDetails={() => handleOpenDetails(job.jobId)}
                />
              ))}
              {errorState ? (
                <Typography
                  sx={{
                    mt: 1,
                    fontFamily: "Rubik",
                    color: "#e00036",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  {errorState}
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1, alignItems: "center" }}>
                {isLoadingMore ? <CircularProgress size={28} sx={{ color: "#0b3d87" }} /> : null}
                <Button
                  variant="outlined"
                  disabled={loadMoreDisabled}
                  onClick={() => loadMore()}
                  sx={{
                    fontFamily: "Rubik",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 3,
                    fontWeight: "bold",
                    borderColor: "#0b3d87",
                    color: "#0b3d87",
                    "&:hover": { borderColor: "#092b6a", backgroundColor: "#e8f0ff" },
                  }}
                >
                  {loadMoreDisabled && !isInitialLoading && imports.length > 0
                    ? "Non ci sono altri risultati"
                    : "Carica altro"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <DialogDettaglioImportazione
          open={Boolean(selectedJobId)}
          onClose={handleCloseDialog}
          statistics={importStatistics}
          issues={issues}
          isLoading={isDetailsLoading}
          loadError={detailsError}
        />
    </Box>
  );
};

export default Caricamenti;
